/**
 * "Credentials": one picker per secret run setting (an EMR token, say). The
 * experiment stores only the credential's id; the value is typed once, sent on
 * create, and never shown again — the list shows its name and hint. Every
 * saved credential is offered.
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { errorText } from "@/components/ui/states";
import type { RunSetting } from "@/features/agents/types";
import { useCreateCredential, useCredentials } from "@/features/credentials/hooks";
import type { Credential } from "@/features/credentials/types";
import { SettingLabel, settingHint } from "./run-settings-section";
import { Select } from "@/components/ui/select";

function AddCredential({ forKey, onAdded }: { forKey: string; onAdded: (c: Credential) => void }) {
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const create = useCreateCredential();
  const id = `ne-cred-${forKey}`;
  const save = () =>
    create.mutate(
      { name: name.trim(), value },
      { onSuccess: (c) => (setValue(""), setName(""), onAdded(c)) },
    );
  return (
    <div
      className="flex flex-col gap-2 rounded-lg bg-well p-3"
      role="group"
      aria-label={`New credential for ${forKey}`}
    >
      <Field label="Credential name" htmlFor={`${id}-name`}>
        <input
          id={`${id}-name`}
          className="inp w-full"
          value={name}
          placeholder="e.g. MedStar EMR sandbox"
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <Field
        label="Value"
        htmlFor={`${id}-value`}
        hint="Stored encrypted. It is never shown again, only its last four characters."
        error={create.isError ? errorText(create.error, "Could not save the credential.") : undefined}
      >
        <input
          id={`${id}-value`}
          type="password"
          autoComplete="off"
          className="inp w-full font-mono"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
      </Field>
      <Button
        size="sm"
        className="self-start"
        disabled={!name.trim() || !value || create.isPending}
        onClick={save}
      >
        Save credential
      </Button>
    </div>
  );
}

/** Pick a saved credential, or add one inline; the experiment keeps only its id. */
export function CredentialSelect({
  forKey,
  label,
  value,
  onChange,
}: {
  forKey: string;
  label: string;
  value: string;
  onChange: (id: string) => void;
}) {
  const credentials = useCredentials();
  const [adding, setAdding] = useState(false);
  const list = credentials.data ?? [];
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <Select
          id={`ne-secret-${forKey}`}
          label={label}
          className="min-w-0 flex-1"
          buttonClassName="h-8 text-xs"
          value={value}
          onChange={onChange}
          options={[
            {
              value: "",
              label: credentials.isPending ? "Loading…" : list.length ? "None" : "No saved credentials",
            },
            ...list.map((c) => ({ value: c.id, label: `${c.name} · ${c.hint}` })),
          ]}
        />
        <button
          type="button"
          className="link shrink-0 text-xs"
          aria-expanded={adding}
          onClick={() => setAdding((a) => !a)}
        >
          {adding ? "Cancel" : "Add credential"}
        </button>
      </div>
      {adding && <AddCredential forKey={forKey} onAdded={(c) => (onChange(c.id), setAdding(false))} />}
    </div>
  );
}

function SecretPicker({
  setting,
  value,
  onChange,
}: {
  setting: RunSetting;
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <Field
      label={<SettingLabel setting={setting} />}
      htmlFor={`ne-secret-${setting.key}`}
      hint={settingHint(setting)}
    >
      <CredentialSelect forKey={setting.key} label={setting.label} value={value} onChange={onChange} />
    </Field>
  );
}

export function CredentialsSection({
  settings,
  refs,
  onChange,
}: {
  settings: RunSetting[];
  refs: Record<string, string>;
  onChange: (key: string, id: string) => void;
}) {
  if (settings.length === 0) return null;
  return (
    <fieldset className="flex flex-col gap-3 rounded-[9px] border border-line p-3.5">
      <legend className="px-1 text-[13px] font-semibold">Credentials</legend>
      <p className="-mt-1 text-xs text-muted">
        Passed to the agent at call time. Never saved in the experiment, a log or a judge prompt.
      </p>
      {settings.map((s) => (
        <SecretPicker
          key={s.key}
          setting={s}
          value={refs[s.key] ?? ""}
          onChange={(id) => onChange(s.key, id)}
        />
      ))}
    </fieldset>
  );
}
